import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, ReplaySubject, Subject, take } from 'rxjs';
import {
	BlindData,
	BlindProperties,
	NetworkStatus,
	QuizCloseButton,
	IBlindSelect,
	SectionKey,
	DialogState,
	BlindPartsAvailability,
	BlindConfigurationStatuses,
	PriceDetails,
	BlindsValidity,
	MaterialType,
} from '@root/app.interfaces';

@Injectable({
	providedIn: 'root',
})
export class ShareService {
	private country = new Subject<string>();
	private blindStatus = new Subject<boolean>();
	private blindUnselect = new Subject<boolean>();
	private blindSelect = new Subject<IBlindSelect>();
	private blindEmpty = new Subject<boolean>();
	private blindName = new Subject<string>();
	private blindSizeChanged = new Subject<any>();
	private blindDelete = new Subject<number>();
	private openingDeselect = new Subject<number>();
	private dataSource = new Subject<BlindData[]>();
	private retailerInfo = new Subject<any>();
	private embeddedRetailer = new Subject<boolean>();
	private embeddedStatus = new Subject<boolean>();
	private linkSessionKey = new Subject<any>();

	private operationName = new Subject<string>();
	private materialType = new Subject<{ blindId: number; type: MaterialType }>();
	private sessionBlindsData = new Subject<any>();
	private screenShotBLindTemp = new Subject<string>();
	private emptyBlindData = new Subject<any>();
	private cursorPointer = new Subject<any>();
	private cursorGrab = new Subject<any>();
	private retailerRequest = new Subject<boolean>();
	private userTime = new Subject<any>();
	private actionBackPage = new Subject<any>();
	private modelLoaded = new Subject<boolean>();
	private sceneCreated = new Subject<string>();
	private screenShotSaved = new Subject<any>();
	private screenShotAborted = new Subject<boolean>();
	private screenShotStatus = new Subject<boolean>();
	private screenShotLoadedOnServer = new Subject<boolean>();
	private loadingSpinner = new Subject<any>();
	private blindsUpdated = new Subject<any>();
	private materialColorChanged = new Subject<any>();
	private blindType = new Subject<BlindProperties>();
	private accordionType = new Subject<string>();
	private currentBlindByType = new Subject<string>();
	private accordionMobileBlindList = new Subject<boolean>();
	private accordionBlindById = new Subject<number>();

	private viewType = new Subject<string>();

	private iVShutterControl = new Subject<boolean>();
	private iVResetStatus = new Subject<boolean>();
	private quizStatus = new Subject<QuizCloseButton>();
	private feedbackTooltipStatus = new Subject<boolean>();

	private aRStatus = new Subject<string>();
	private checkExit = new Subject<any>();
	private networkStatus = new Subject<NetworkStatus>();
	private addBlind = new Subject<boolean>();
	private selectedModel = new Subject<string>();
	private taskbarPanel = new Subject<boolean>();
	private taskbarControls = new Subject<boolean>();
	private toolBarEvent = new Subject<boolean>();
	private collapseToolBar = new Subject<boolean>();
	private gizmoControlAction = new Subject<boolean>();
	private selectOpeningMode = new BehaviorSubject<boolean>(false);
	private detectionStart = new Subject<void>();
	private detectionDone = new BehaviorSubject<boolean>(false);
	private isAnOpeningSelected = new Subject<boolean>();
	private bboxApplied = new ReplaySubject<string>(1);
	private safeToFit = new ReplaySubject<string>(1);
	private openingFitReset = new BehaviorSubject<boolean>(false);
	private detectionError = new Subject<Error>();
	private blindDisplayStatusesRequest = new Subject<void>();
	private blindDisplayStatusesResponse = new BehaviorSubject<Record<string, boolean>>(null);
	private blindsConfigured = new BehaviorSubject<boolean>(false);
	private blindsConfigurationStatuses = new BehaviorSubject<BlindConfigurationStatuses>(null);
	private openSection = new Subject<{ blindId: number; section: SectionKey; openToolBar?: boolean }>();
	private closeSection = new Subject<{ blindId: number; section: SectionKey }>();
	private accordionFinished = new Subject<number>();
	private dialogState = new Subject<DialogState>();
	private partAvailability = new Subject<BlindPartsAvailability>();
	private blindsValidityStatus = new BehaviorSubject<BlindsValidity>({});
	private blindsConfigReady = new BehaviorSubject<boolean>(null);

	private priceDetails = new BehaviorSubject<PriceDetails | null>(null);

	getCountry = this.country.asObservable();
	getStatus = this.blindStatus.asObservable();
	getBlindName = this.blindName.asObservable();
	getBlindSizeChanged = this.blindSizeChanged.asObservable();
	getBlindDelete = this.blindDelete.asObservable();
	getOpeningDeselect = this.openingDeselect.asObservable();
	getBlindUnselect = this.blindUnselect.asObservable();
	getCurrentBlindByType = this.currentBlindByType.asObservable();
	getBlindSelect = this.blindSelect.asObservable();
	getBlindEmpty = this.blindEmpty.asObservable();
	currentData = this.dataSource.asObservable();
	getRetailerInfo = this.retailerInfo.asObservable();
	getEmbeddedRetailer = this.embeddedRetailer.asObservable();
	getEmptyBlindData = this.emptyBlindData.asObservable();
	getEmbeddedStatus = this.embeddedStatus.asObservable();
	getOperationName = this.operationName.asObservable();
	getMaterialType = this.materialType.asObservable();
	getLinkSessionKey = this.linkSessionKey.asObservable();
	getSessionBlindsData = this.sessionBlindsData.asObservable();
	getScreenShotBLindTemp = this.screenShotBLindTemp.asObservable();
	getCursorPointer = this.cursorPointer.asObservable();
	getCursorGrab = this.cursorGrab.asObservable();
	getRetailerRequest = this.retailerRequest.asObservable();
	getUserTime = this.userTime.asObservable();
	getActionBackPage = this.actionBackPage.asObservable();
	getModelLoaded = this.modelLoaded.asObservable();
	getSceneCreated = this.sceneCreated.asObservable();
	getScreenShotSaved = this.screenShotSaved.asObservable();
	getScreenShotAborted = this.screenShotAborted.asObservable();
	getScreenShotStatus = this.screenShotStatus.asObservable();
	getScreenShotLoadedOnServer = this.screenShotLoadedOnServer.asObservable();
	getLoadingSpinner = this.loadingSpinner.asObservable();
	getBlindsUpdated = this.blindsUpdated.asObservable();
	getMaterialColorChanged = this.materialColorChanged.asObservable();
	getBlindType = this.blindType.asObservable();
	getAccordionType = this.accordionType.asObservable();
	getAccordionMobileBlindList = this.accordionMobileBlindList.asObservable();
	getAccordionBlindById = this.accordionBlindById.asObservable();

	getViewType = this.viewType.asObservable();
	getQuizStatus = this.quizStatus.asObservable();
	getFeedbackTooltipStatus = this.feedbackTooltipStatus.asObservable();

	getIVShutterControl = this.iVShutterControl.asObservable();
	getIVResetStatus = this.iVResetStatus.asObservable();
	getARStatus = this.aRStatus.asObservable();
	getCheckExit = this.checkExit.asObservable();
	getNetworkStatus = this.networkStatus.asObservable();
	getAddBlind = this.addBlind.asObservable();
	getSelectedModel = this.selectedModel.asObservable();
	getTaskbarPanel = this.taskbarPanel.asObservable();
	getTaskbarControls = this.taskbarControls.asObservable();
	getToolBarEvent = this.toolBarEvent.asObservable();
	getCollapseToolBar = this.collapseToolBar.asObservable();
	getGizmoControlAction = this.gizmoControlAction.asObservable();
	getSelectOpeningMode = this.selectOpeningMode.asObservable();
	getDetectionStart = this.detectionStart.asObservable();
	getDetectionDone = this.detectionDone.asObservable();
	getIsAnOpeningSelected = this.isAnOpeningSelected.asObservable();
	getBboxApplied = this.bboxApplied.asObservable();
	getSafeToFit = this.safeToFit.asObservable();
	getDetectionError = this.detectionError.asObservable();
	getBlindDisplayStatusesRequest = this.blindDisplayStatusesRequest.asObservable();
	getblindDisplayStatusesResponse = this.blindDisplayStatusesResponse.asObservable();
	getBlindsConfigured = this.blindsConfigured.asObservable();
	getBlindsConfigurationStatuses = this.blindsConfigurationStatuses.asObservable();
	getOpenSection = this.openSection.asObservable();
	getCloseSection = this.closeSection.asObservable();
	getAccordionFinished = this.accordionFinished.asObservable();
	getDialogState = this.dialogState.asObservable();
	getPartAvailability = this.partAvailability.asObservable();
	getBlindsValidityStatus = this.blindsValidityStatus.asObservable();
	isBlindsConfigReady = this.blindsConfigReady.asObservable();

	getPriceDetails = this.priceDetails.asObservable();

	setCountry(country: string): void {
		this.country.next(country);
	}

	setStatus(status: boolean): void {
		this.blindStatus.next(status);
	}

	setBlindName(name: string): void {
		this.blindName.next(name);
	}

	setBlindSizeChanged(size: any): void {
		this.blindSizeChanged.next(size);
	}

	setBlindDelete(blindId: number): void {
		this.blindDelete.next(blindId);
	}

	setOpeningDeselect(openingId: number): void {
		this.openingDeselect.next(openingId);
	}

	setBlindUnselect(select: boolean): void {
		this.blindUnselect.next(select);
	}

	setBlindSelect(select: IBlindSelect): void {
		this.blindSelect.next(select);
	}

	setBlindEmpty(select: boolean): void {
		this.blindEmpty.next(select);
	}

	changeData(data: any): void {
		this.dataSource.next(data);
	}

	setOperationName(select: string): void {
		this.operationName.next(select);
	}

	setMaterialType(blindMaterial: { blindId: number; type: MaterialType }): void {
		this.materialType.next(blindMaterial);
	}

	setRetailerInfo(data: any): void {
		this.retailerInfo.next(data);
	}

	setEmbeddedRetailer(data: any): void {
		this.embeddedRetailer.next(data);
	}

	setEmbeddedStatus(status: boolean): void {
		this.embeddedStatus.next(status);
	}

	setLinkSessionKey(data: any): void {
		this.linkSessionKey.next(data);
	}

	setSessionBlindsData(data: any): void {
		this.sessionBlindsData.next(data);
	}

	setScreenShotBLindTemp(data: string): void {
		this.screenShotBLindTemp.next(data);
	}

	setEmptyBlindData(data: any): void {
		this.emptyBlindData.next(data);
	}

	setCursorPointer(data: any): void {
		this.cursorPointer.next(data);
	}

	setCursorGrab(data: any): void {
		this.cursorGrab.next(data);
	}

	setRetailerRequest(status: boolean) {
		this.retailerRequest.next(status);
	}

	setUserTime(time: any) {
		this.userTime.next(time);
	}

	setActionBackPage(blindData: boolean) {
		this.actionBackPage.next(blindData);
	}

	setModelLoaded(status: any) {
		this.modelLoaded.next(status);
	}

	setSceneCreated(status: string) {
		this.sceneCreated.next(status);
	}

	setScreenShotSaved(status: any) {
		this.screenShotSaved.next(status);
	}

	setScreenShotAborted(status: any) {
		this.screenShotAborted.next(status);
	}

	setScreenShotStatus(status: any) {
		this.screenShotStatus.next(status);
	}

	setScreenShotLoadedOnServer(status: boolean) {
		this.screenShotLoadedOnServer.next(status);
	}

	setLoadingSpinner(status: any) {
		this.loadingSpinner.next(status);
	}

	setBlindsUpdated(status: any) {
		this.blindsUpdated.next(status);
	}

	setMaterialColorChanged(color: any) {
		this.materialColorChanged.next(color);
	}

	setBlindType(data) {
		this.blindType.next(data);
	}

	setAccordionType(type: string) {
		this.accordionType.next(type);
	}

	setViewType(type: string) {
		this.viewType.next(type);
	}

	setIVShutterControl(status: boolean) {
		this.iVShutterControl.next(status);
	}

	setIVResetStatus(status: boolean) {
		this.iVResetStatus.next(status);
	}

	setARStatus(status: string) {
		this.aRStatus.next(status);
	}

	setQuizStatus(data: QuizCloseButton) {
		this.quizStatus.next(data);
	}

	setCurrentBlindByType(type: string) {
		this.currentBlindByType.next(type);
	}

	setCheckExit(type: any) {
		this.checkExit.next(type);
	}

	setNetworkStatus(status: NetworkStatus) {
		this.networkStatus.next(status);
	}

	setAddBlind(saveScreenShot: boolean) {
		this.addBlind.next(saveScreenShot);
	}

	setSelectedModel(id: string) {
		this.selectedModel.next(id);
	}

	setTaskbarPanel(status: boolean) {
		this.taskbarPanel.next(status);
	}

	setTaskbarControls(status: boolean) {
		this.taskbarControls.next(status);
	}

	setToolBarEvent(status: boolean) {
		this.toolBarEvent.next(status);
	}

	setCollapseToolBar(status: boolean) {
		this.collapseToolBar.next(status);
	}

	setGizmoControlAction(status: boolean) {
		this.gizmoControlAction.next(status);
	}

	setAccordionMobileBlindList(status: boolean) {
		this.accordionMobileBlindList.next(status);
	}

	setAccordionBlindById(id: number) {
		this.accordionBlindById.next(id);
	}

	setFeedbackTooltipStatus(status: boolean) {
		this.feedbackTooltipStatus.next(status);
	}

	setSelectOpeningMode(status: boolean) {
		this.selectOpeningMode.next(status);
	}

	setDetectionStart() {
		this.detectionStart.next();
	}

	setDetectionDone(status: boolean) {
		this.detectionDone.next(status);
	}

	getSelectOpeningModeValue(): boolean {
		return this.selectOpeningMode.getValue();
	}

	setIsAnOpeningSelected(status: boolean) {
		this.isAnOpeningSelected.next(status);
	}

	setBboxApplied(id: string) {
		this.bboxApplied.next(id);
	}

	setSafeToFit(id: string) {
		this.safeToFit.next(id);
	}

	setOpeningFitReset(status: boolean) {
		this.openingFitReset.next(status);
	}

	getOpeningFitResetValue(): boolean {
		return this.openingFitReset.getValue();
	}

	setDetectionError(err: Error) {
		this.detectionError.next(err);
	}

	async setblindDisplayStatusesRequest(): Promise<Record<string, boolean>> {
		this.blindDisplayStatusesRequest.next();

		return firstValueFrom(this.blindDisplayStatusesResponse.pipe(take(1)));
	}

	setblindDisplayStatusesResponse(payload: Record<string, boolean>, reset = false): void {
		// I hate this but it 'seems' to work and I don't have time for something better
		const current = this.blindDisplayStatusesResponse.getValue();

		if (reset || !current || Object.keys(payload).length >= Object.keys(current).length) {
			this.blindDisplayStatusesResponse.next(payload);
		}
	}

	getblindDisplayStatusesResponseValue() {
		return this.blindDisplayStatusesResponse.getValue();
	}

	setBlindsConfigured(status: boolean) {
		return this.blindsConfigured.next(status);
	}

	setBlindsConfigurationStatuses(blindStates: BlindConfigurationStatuses) {
		return this.blindsConfigurationStatuses.next(blindStates);
	}

	getBlindsConfiguredValue(): boolean {
		return this.blindsConfigured.getValue();
	}

	setOpenSection(data: { blindId: number; section: SectionKey; openToolBar?: boolean }) {
		this.openSection.next(data);
	}

	setCloseSection(data: { blindId: number; section: SectionKey }) {
		this.closeSection.next(data);
	}

	setAccordionFinished(blindId: number) {
		this.accordionFinished.next(blindId);
	}

	setDialogState(state: DialogState): void {
		this.dialogState.next(state);
	}

	setPartAvailability(value: BlindPartsAvailability): void {
		this.partAvailability.next(value);
	}

	setBlindsValidityStatus(validity: BlindsValidity): void {
		this.blindsValidityStatus.next(validity);
	}

	setPriceDetails(priceDetails: PriceDetails): void {
		this.priceDetails.next(priceDetails);
	}

	setBlindsConfigReady(isReady: boolean): void {
		this.blindsConfigReady.next(isReady);
	}
}
